"use strict";

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import RefreshIcon from "@mui/icons-material/Refresh";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import TuneIcon from "@mui/icons-material/Tune";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    CHARACTER_STATUSES_URL,
    EVALUATE_ALL_VOICES,
    GET_CHARACTERS_FOR_PROJECT,
    GET_SPEECHS_DETAILS_FOR_PROJECT,
    TRAIN_SINGLE_VOICE,
} from "../ApiEndpoints.js";
import Loader from "../Components/Loader.js";
import { Store } from "../Store";
import {
    AVAILABLE_RATINGS,
    DEFAULT_EMOTIONS,
} from "../Data/dialogueCodes.js";

const CONTENT_MAX_WIDTH = 1180;

const voiceStatusMeta = {
    verified: {
        label: "Verified",
        color: "#05603a",
        backgroundColor: "#d1fadf",
        borderColor: "#75e0a7",
        icon: CheckCircleIcon,
    },
    available: {
        label: "Available",
        color: "#175a78",
        backgroundColor: "#e8f5fb",
        borderColor: "#9fd3e8",
    },
    censored: {
        label: "Censored",
        color: "#8a4b00",
        backgroundColor: "#fff0db",
        borderColor: "#efbd7a",
    },
    not_trained: {
        label: "Not Trained",
        color: "#596273",
        backgroundColor: "#f3f5f7",
        borderColor: "#d8dde5",
    },
};

const getCharacterEmotionVoice = (characterVoiceStatus, speaker, emotion) => {
    return characterVoiceStatus?.[speaker]?.emotions?.[emotion] || null;
};

const getCharacterEmotionVoiceStatus = (characterVoiceStatus, speaker, emotion) => {
    const emotionVoice = getCharacterEmotionVoice(characterVoiceStatus, speaker, emotion);

    if (!emotionVoice) {
        return "not_trained";
    }

    const apiStatus = String(emotionVoice.status || "")
        .trim()
        .toLowerCase();

    if (apiStatus === "censored") {
        return "censored";
    }

    if (apiStatus === "verified" || emotionVoice.voiceId || emotionVoice.voice_id) {
        return "verified";
    }

    if (apiStatus === "available" || apiStatus === "trained") {
        return "available";
    }

    return "not_trained";
};

const getVoiceId = (characterVoiceStatus, speaker, emotion) => {
    const emotionVoice = getCharacterEmotionVoice(characterVoiceStatus, speaker, emotion);

    return emotionVoice?.voiceId || emotionVoice?.voice_id || "";
};

const getVoiceRowKey = (speaker, emotion) => {
    return `${speaker || "unknown"}::${emotion || "unknown"}`;
};

const normalizeEmotionLabel = (emotion) => {
    return emotion.replace(/_/g, " ");
};

const buildCharacterProgressEntries = (transcriptionStats, characters) => {
    if (!transcriptionStats?.charactersStats) {
        return [];
    }

    const characterStates = transcriptionStats.characterStates ?? {};

    return Object.entries(transcriptionStats.charactersStats)
        .map(([speaker, stats]) => {
            const dialogues = Object.values(stats.dialogues || {});
            const ratingCounts = {};
            const emotionCounts = {};
            let unratedCount = 0;

            AVAILABLE_RATINGS.forEach((rating) => {
                ratingCounts[rating] = 0;
            });

            DEFAULT_EMOTIONS.forEach((emotion) => {
                emotionCounts[emotion] = { total: 0, highRated: 0 };
            });

            dialogues.forEach((dialogue) => {
                const ratingValueRaw = dialogue.originalAudioRating;
                const ratingValue = Number(ratingValueRaw);
                const ratingIsProvided =
                    ratingValueRaw !== null &&
                    ratingValueRaw !== undefined &&
                    ratingValueRaw !== "";
                const ratingIsPositiveNumber =
                    ratingIsProvided && !Number.isNaN(ratingValue) && ratingValue > 0;

                if (ratingIsPositiveNumber) {
                    if (ratingCounts[ratingValue] === undefined) {
                        ratingCounts[ratingValue] = 0;
                    }

                    ratingCounts[ratingValue] += 1;
                }

                const emotionValue = dialogue.originalAudioEmotion;
                if (emotionValue) {
                    if (emotionCounts[emotionValue] === undefined) {
                        emotionCounts[emotionValue] = { total: 0, highRated: 0 };
                    }

                    emotionCounts[emotionValue].total += 1;

                    if (ratingIsPositiveNumber && ratingValue >= 4) {
                        emotionCounts[emotionValue].highRated += 1;
                    }
                }
            });

            const characterState = characterStates[speaker];
            if (
                characterState &&
                Object.prototype.hasOwnProperty.call(characterState, "unratedDialogueCount")
            ) {
                const unratedValue = Number(characterState.unratedDialogueCount);
                if (Number.isFinite(unratedValue)) {
                    unratedCount = unratedValue;
                }
            }

            const matchingCharacter = characters.find((char) => char.speaker === speaker);
            const displayName = matchingCharacter
                ? matchingCharacter.displayName || matchingCharacter.speaker
                : speaker;

            return {
                speaker,
                displayName,
                ratingCounts,
                emotionCounts,
                dialogueCount: dialogues.length,
                isImportedVoice: Boolean(matchingCharacter?.projectId),
                unratedCount,
            };
        })
        .filter((entry) => {
            const normalizedName = `${entry.displayName || entry.speaker}`.trim();
            return normalizedName !== "" || entry.dialogueCount > 0;
        });
};

const VoiceStatusPill = ({ status }) => {
    const meta = voiceStatusMeta[status] || voiceStatusMeta.not_trained;
    const StatusIcon = meta.icon;

    return (
        <Box
            component="span"
            sx={{
                minHeight: 22,
                px: 1,
                py: 0.35,
                borderRadius: "999px",
                border: `1px solid ${meta.borderColor}`,
                background: meta.backgroundColor,
                color: meta.color,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: "0.04em",
                lineHeight: 1,
                whiteSpace: "nowrap",
                textTransform: "uppercase",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.45,
            }}
        >
            {StatusIcon && <StatusIcon sx={{ fontSize: 13 }} />}
            {meta.label}
        </Box>
    );
};

const SummaryTile = ({ label, value, icon, alert = false }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: "18px",
                border: alert
                    ? "1px solid rgba(239,68,68,0.28)"
                    : "1px solid rgba(223, 230, 238, 0.95)",
                backgroundColor: alert
                    ? "rgba(254,242,242,0.94)"
                    : "rgba(255,255,255,0.94)",
                minHeight: 88,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: alert ? "#dc2626" : "#1f6f96",
                    backgroundColor: alert
                        ? "rgba(239,68,68,0.10)"
                        : "rgba(31,111,150,0.09)",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 900,
                        color: alert ? "#dc2626" : "#101828",
                        lineHeight: 1.1,
                    }}
                >
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085", fontWeight: 700 }}>
                    {label}
                </Typography>
            </Box>
        </Paper>
    );
};

const CharacterVoiceCard = ({
    entry,
    emotionKeys,
    characterVoiceStatus,
    onEditEmotion,
    onTrainEmotion,
    trainingVoiceKey,
}) => {
    const voiceStatusCounts = emotionKeys.reduce(
        (counts, emotion) => {
            const status = getCharacterEmotionVoiceStatus(
                characterVoiceStatus,
                entry.speaker,
                emotion
            );

            return {
                ...counts,
                [status]: (counts[status] || 0) + 1,
            };
        },
        {
            verified: 0,
            available: 0,
            not_trained: 0,
            censored: 0,
        }
    );

    const sampleCount = emotionKeys.reduce((total, emotion) => {
        return total + (entry.emotionCounts[emotion]?.highRated ?? 0);
    }, 0);

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid rgba(223, 230, 238, 0.95)",
                borderRadius: "22px",
                backgroundColor: "rgba(255,255,255,0.96)",
                boxShadow: "0 16px 38px rgba(15, 23, 42, 0.07)",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                    background:
                        "linear-gradient(135deg, rgba(31,111,150,0.08), rgba(255,255,255,0.96))",
                    borderBottom: "1px solid #edf0f5",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 900,
                            color: "#101828",
                            lineHeight: 1.15,
                            textTransform: "capitalize",
                        }}
                    >
                        {entry.displayName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#667085", mt: 0.35 }}>
                        {entry.dialogueCount} dialogue entries · {entry.unratedCount} unrated
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                    <Chip
                        size="small"
                        label={`${sampleCount} samples`}
                        sx={{
                            fontWeight: 850,
                            color: "#1f6f96",
                            backgroundColor: "rgba(31,111,150,0.10)",
                        }}
                    />
                    {voiceStatusCounts.verified > 0 && (
                        <Chip
                            size="small"
                            label={`${voiceStatusCounts.verified} verified`}
                            sx={{
                                fontWeight: 850,
                                color: voiceStatusMeta.verified.color,
                                backgroundColor: voiceStatusMeta.verified.backgroundColor,
                            }}
                        />
                    )}
                    {voiceStatusCounts.available > 0 && (
                        <Chip
                            size="small"
                            label={`${voiceStatusCounts.available} available`}
                            sx={{
                                fontWeight: 850,
                                color: voiceStatusMeta.available.color,
                                backgroundColor: voiceStatusMeta.available.backgroundColor,
                            }}
                        />
                    )}
                    {voiceStatusCounts.not_trained > 0 && (
                        <Chip
                            size="small"
                            label={`${voiceStatusCounts.not_trained} not trained`}
                            sx={{
                                fontWeight: 850,
                                color: voiceStatusMeta.not_trained.color,
                                backgroundColor: voiceStatusMeta.not_trained.backgroundColor,
                            }}
                        />
                    )}
                </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                {entry.isImportedVoice ? (
                    <Box
                        sx={{
                            minHeight: 96,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "#667085",
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 750 }}>
                            Imported Voice
                        </Typography>
                    </Box>
                ) : (
                    <Stack divider={<Divider flexItem />} spacing={0}>
                        {emotionKeys.map((emotion) => {
                            const emotionStats = entry.emotionCounts[emotion] ?? {
                                total: 0,
                                highRated: 0,
                            };
                            const voiceStatus = getCharacterEmotionVoiceStatus(
                                characterVoiceStatus,
                                entry.speaker,
                                emotion
                            );
                            const voiceId = getVoiceId(characterVoiceStatus, entry.speaker, emotion);
                            const rowKey = getVoiceRowKey(entry.speaker, emotion);
                            const isTraining = trainingVoiceKey === rowKey;
                            const isTrained = voiceStatus !== "not_trained";
                            const trainingLabel = isTrained ? "Retrain" : "Train";

                            return (
                                <Box
                                    key={`${entry.speaker}-${emotion}`}
                                    sx={{
                                        py: 1.35,
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "minmax(0, 1fr)",
                                            md: "minmax(140px, 0.8fr) minmax(0, 1fr) auto",
                                        },
                                        alignItems: "center",
                                        gap: { xs: 1, md: 2 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 850,
                                                color: "#101828",
                                                textTransform: "capitalize",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {normalizeEmotionLabel(emotion)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#667085" }}>
                                            {emotionStats.highRated ?? 0} samples available of {emotionStats.total ?? 0}
                                        </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "#475467", fontWeight: 750 }}
                                        >
                                            {emotionStats.highRated ?? 0} samples
                                        </Typography>
                                        <VoiceStatusPill status={voiceStatus} />
                                        {voiceId && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#667085",
                                                    fontFamily: "monospace",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: { xs: "100%", md: 220 },
                                                }}
                                            >
                                                {voiceId}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            justifySelf: { xs: "flex-start", md: "end" },
                                            flexWrap: "wrap",
                                            rowGap: 1,
                                        }}
                                    >
                                        <Button
                                            variant={isTrained ? "outlined" : "contained"}
                                            size="small"
                                            startIcon={
                                                isTraining ? (
                                                    <CircularProgress size={14} color="inherit" />
                                                ) : (
                                                    <GraphicEqIcon fontSize="small" />
                                                )
                                            }
                                            onClick={() => onTrainEmotion(entry, emotion, isTrained)}
                                            disabled={isTraining}
                                            sx={{
                                                borderRadius: "999px",
                                                borderColor: "rgba(31,111,150,0.32)",
                                                backgroundColor: isTrained ? "transparent" : "#1f6f96",
                                                color: isTrained ? "#1f6f96" : "#ffffff",
                                                fontWeight: 850,
                                                textTransform: "none",
                                                minWidth: 92,
                                                "&:hover": {
                                                    backgroundColor: isTrained
                                                        ? "rgba(31,111,150,0.08)"
                                                        : "#185a79",
                                                },
                                            }}
                                        >
                                            {trainingLabel}
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditIcon fontSize="small" />}
                                            onClick={() => onEditEmotion(entry, emotion)}
                                            sx={{
                                                borderRadius: "999px",
                                                borderColor: "rgba(31,111,150,0.32)",
                                                color: "#1f6f96",
                                                fontWeight: 850,
                                                textTransform: "none",
                                                minWidth: 88,
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

const ProjectVoicesScreen = () => {
    const { id: routeProjectId } = useParams();
    const navigate = useNavigate();
    const { state } = useContext(Store);
    const { projectFile, userInfo } = state;
    const projectId = routeProjectId || projectFile?.id;

    const [isLoading, setIsLoading] = useState(false);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [trainingVoiceKey, setTrainingVoiceKey] = useState("");
    const [characters, setCharacters] = useState([]);
    const [transcriptionStats, setTranscriptionStats] = useState(null);
    const [characterVoiceStatus, setCharacterVoiceStatus] = useState({});

    const loadVoicesPageData = useCallback(async () => {
        if (!projectId) {
            return;
        }

        setIsLoading(true);

        try {
            const [charactersResponse, statsResponse, statusesResponse] = await Promise.all([
                axios.post(GET_CHARACTERS_FOR_PROJECT, { projectId }),
                axios.post(GET_SPEECHS_DETAILS_FOR_PROJECT, { projectId }),
                axios.post(CHARACTER_STATUSES_URL, { projectId }),
            ]);

            const loadedCharacters = Array.isArray(charactersResponse.data?.characters)
                ? charactersResponse.data.characters
                : [];

            setCharacters(loadedCharacters);
            setTranscriptionStats(statsResponse.data);
            setCharacterVoiceStatus(
                statusesResponse.data?.status === true
                    ? statusesResponse.data.voiceStatus || {}
                    : {}
            );
        } catch (error) {
            console.error("Failed to load project voices:", error);
            toast.error("Failed to load project voices.");
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadVoicesPageData();
    }, [loadVoicesPageData]);

    const characterProgressEntries = useMemo(() => {
        return buildCharacterProgressEntries(transcriptionStats, characters);
    }, [transcriptionStats, characters]);

    const emotionKeys = useMemo(() => {
        const emotionsFromStats = characterProgressEntries.flatMap((entry) => {
            return Object.keys(entry.emotionCounts || {});
        });

        const emotionsFromVoices = Object.values(characterVoiceStatus).flatMap((status) => {
            return Object.keys(status?.emotions || {});
        });

        return [...new Set([...DEFAULT_EMOTIONS, ...emotionsFromStats, ...emotionsFromVoices])];
    }, [characterProgressEntries, characterVoiceStatus]);

    const summary = useMemo(() => {
        let availableCount = 0;
        let verifiedCount = 0;
        let charactersWithoutVoices = 0;

        characterProgressEntries.forEach((entry) => {
            let characterHasVoice = entry.isImportedVoice;

            emotionKeys.forEach((emotion) => {
                const status = getCharacterEmotionVoiceStatus(
                    characterVoiceStatus,
                    entry.speaker,
                    emotion
                );

                if (status === "verified") {
                    verifiedCount += 1;
                    characterHasVoice = true;
                } else if (status === "available") {
                    availableCount += 1;
                    characterHasVoice = true;
                }
            });

            if (!characterHasVoice) {
                charactersWithoutVoices += 1;
            }
        });

        return {
            characters: characterProgressEntries.length,
            available: availableCount + verifiedCount,
            charactersWithoutVoices,
        };
    }, [characterProgressEntries, characterVoiceStatus, emotionKeys]);

    const handleEvaluateAllVoices = async () => {
        if (!projectId) {
            return;
        }

        try {
            setIsEvaluating(true);
            await axios.post(EVALUATE_ALL_VOICES, { projectId });
            toast.success("The process of evaluating all voices has been activated");
            await loadVoicesPageData();
        } catch (error) {
            console.error("Failed to evaluate voices:", error);
            toast.error("An Error has occured while evaluating the voices");
        } finally {
            setIsEvaluating(false);
        }
    };

    const handleEditEmotion = (entry, emotion) => {
        navigate(`/projects/${projectId}/voice-mixing`, {
            state: {
                speaker: entry.speaker,
                emotion,
            },
        });
    };

    const handleTrainEmotion = async (entry, emotion, isTrained) => {
        if (!projectId) {
            return;
        }

        const rowKey = getVoiceRowKey(entry.speaker, emotion);
        const requestConfig = userInfo?.token
            ? {
                  headers: {
                      Authorization: `Bearer ${userInfo.token}`,
                  },
              }
            : undefined;

        try {
            setTrainingVoiceKey(rowKey);
            const response = await axios.post(
                TRAIN_SINGLE_VOICE,
                {
                    projectId,
                    speaker: entry.speaker,
                    emotion,
                    forceRetrain: isTrained,
                },
                requestConfig
            );

            if (response.data?.skipped) {
                toast.info(response.data.msg || "Voice already exists.");
            } else {
                toast.success(
                    isTrained
                        ? "Voice retraining completed."
                        : "Voice training completed."
                );
            }

            await loadVoicesPageData();
        } catch (error) {
            console.error("Failed to train single voice:", error);
            toast.error(
                error.response?.data?.error ||
                    error.response?.data?.message ||
                    "An Error has occured while training the voice"
            );
        } finally {
            setTrainingVoiceKey("");
        }
    };

    return (
        <Container
            maxWidth={false}
            style={{ fontFamily: "var(--font-body)" }}
            sx={{ py: { xs: 3, md: 5 } }}
        >
            <Loader Load={isLoading} />
            <ToastContainer position="top-center" />

            <Box
                sx={{
                    width: "100%",
                    maxWidth: CONTENT_MAX_WIDTH,
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    px: { xs: 1.5, sm: 2 },
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 46,
                                height: 46,
                                border: "1px solid rgba(255,255,255,0.7)",
                                borderRadius: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 8px 22px rgba(15, 23, 42, 0.1)",
                                background:
                                    "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(245,249,252,0.92))",
                                color: "#1f6f96",
                                flexShrink: 0,
                            }}
                        >
                            <RecordVoiceOverIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: 30, md: 40 },
                                    letterSpacing: 0,
                                    color: "#050b14",
                                    lineHeight: 1.1,
                                }}
                            >
                                Project Voices
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#667085", mt: 0.75 }}>
                                {projectFile?.name || "Character voice readiness"}
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={
                                isEvaluating ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )
                            }
                            onClick={handleEvaluateAllVoices}
                            disabled={isEvaluating || isLoading}
                            sx={{
                                borderRadius: "999px",
                                borderColor: "rgba(31,111,150,0.32)",
                                color: "#1f6f96",
                                fontWeight: 850,
                                textTransform: "none",
                            }}
                        >
                            Evaluate
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<TuneIcon fontSize="small" />}
                            onClick={() => navigate(`/projects/${projectId}/voice-mixing`)}
                            sx={{
                                borderRadius: "999px",
                                backgroundColor: "#1f6f96",
                                color: "#ffffff",
                                fontWeight: 850,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "#185a79",
                                },
                            }}
                        >
                            Voice Mixing
                        </Button>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                    }}
                >
                    <SummaryTile
                        label="Characters"
                        value={summary.characters}
                        icon={<AutoAwesomeIcon fontSize="small" />}
                    />
                    <SummaryTile
                        label="Available voices"
                        value={summary.available}
                        icon={<GraphicEqIcon fontSize="small" />}
                    />
                    <SummaryTile
                        label="Characters without voices"
                        value={summary.charactersWithoutVoices}
                        icon={<SearchOffIcon fontSize="small" />}
                        alert={summary.charactersWithoutVoices > 0}
                    />
                </Box>

                {characterProgressEntries.length === 0 && !isLoading ? (
                    <Paper
                        elevation={0}
                        sx={{
                            border: "1px solid rgba(223, 230, 238, 0.95)",
                            borderRadius: "24px",
                            backgroundColor: "rgba(255,255,255,0.96)",
                            p: { xs: 3, md: 5 },
                            textAlign: "center",
                        }}
                    >
                        <SearchOffIcon sx={{ fontSize: 42, color: "#1f6f96", mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 850 }}>
                            No character voice data yet
                        </Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2}>
                        {characterProgressEntries.map((entry) => (
                            <CharacterVoiceCard
                                key={entry.speaker}
                                entry={entry}
                                emotionKeys={emotionKeys}
                                characterVoiceStatus={characterVoiceStatus}
                                onEditEmotion={handleEditEmotion}
                                onTrainEmotion={handleTrainEmotion}
                                trainingVoiceKey={trainingVoiceKey}
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        </Container>
    );
};

export default ProjectVoicesScreen;
